import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../config';
import './AdminDiscountCodes.css';

const EMPTY_FORM = {
  code: '',
  discountPercent: 20,
  maxUses: 0,
  expiresAt: ''
};

export default function AdminDiscountCodes() {
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);

    useEffect(() => {
        const username = localStorage.getItem('username');
        const isPowerUser = localStorage.getItem('isPowerUser') === 'true';
        if (username !== 'Higgo' || !isPowerUser) {
            navigate('/');
            return;
        }
        fetchCodes();
    }, [navigate]);

  const fetchCodes = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/discount-codes');
      setCodes(res.data.codes || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load discount codes.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!form.code.trim()) { setError('Code name is required.'); return; }
    setCreating(true);
    try {
      await axiosInstance.post('/discount-codes', {
        code: form.code.trim(),
        discountPercent: Number(form.discountPercent),
        maxUses: Number(form.maxUses),
        expiresAt: form.expiresAt || null
      });
      setSuccess(`Code "${form.code.toUpperCase()}" created successfully.`);
      setForm(EMPTY_FORM);
      setShowForm(false);
      fetchCodes();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not create discount code.');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (id, codeStr) => {
    setError('');
    setSuccess('');
    try {
      const res = await axiosInstance.patch(`/discount-codes/${id}/toggle`);
      const next = res.data.isActive;
      setCodes((prev) => prev.map((c) => c._id === id ? { ...c, isActive: next } : c));
      setSuccess(`"${codeStr}" ${next ? 'activated' : 'deactivated'}.`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not toggle code.');
    }
  };

  const handleDelete = async (id, codeStr) => {
    if (!window.confirm(`Permanently delete code "${codeStr}"? This cannot be undone.`)) return;
    setError('');
    setSuccess('');
    try {
      await axiosInstance.delete(`/discount-codes/${id}`);
      setCodes((prev) => prev.filter((c) => c._id !== id));
      setSuccess(`"${codeStr}" deleted.`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not delete code.');
    }
  };

  return (
    <div className="dc-container">
      <div className="dc-header">
        <div>
          <h1>Discount Codes</h1>
          <p className="dc-subtitle">Create and manage subscriber discount codes</p>
        </div>
        <button className="dc-create-btn" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : '+ New Code'}
        </button>
      </div>

      {error && (
        <div className="dc-alert dc-alert-error">
          {error}
          <button onClick={() => setError('')} className="dc-alert-close">×</button>
        </div>
      )}
      {success && (
        <div className="dc-alert dc-alert-success">
          {success}
          <button onClick={() => setSuccess('')} className="dc-alert-close">×</button>
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <form className="dc-form" onSubmit={handleCreate}>
          <h2>New Discount Code</h2>
          <div className="dc-form-grid">
            <div className="dc-field">
              <label>Code *</label>
              <input
                type="text"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="e.g. LAUNCH20"
                maxLength={30}
              />
            </div>
            <div className="dc-field">
              <label>Discount %</label>
              <input
                type="number"
                min={1}
                max={100}
                value={form.discountPercent}
                onChange={(e) => setForm({ ...form, discountPercent: e.target.value })}
              />
            </div>
            <div className="dc-field">
              <label>Max Uses (0 = unlimited)</label>
              <input
                type="number"
                min={0}
                value={form.maxUses}
                onChange={(e) => setForm({ ...form, maxUses: e.target.value })}
              />
            </div>
            <div className="dc-field">
              <label>Expires (optional)</label>
              <input
                type="date"
                value={form.expiresAt}
                onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
              />
            </div>
          </div>
          <button type="submit" className="dc-submit-btn" disabled={creating}>
            {creating ? 'Creating…' : 'Create Code'}
          </button>
        </form>
      )}

      {/* Stats bar */}
      <div className="dc-stats">
        <div className="dc-stat"><span>{codes.length}</span>Total Codes</div>
        <div className="dc-stat"><span>{codes.filter((c) => c.isActive).length}</span>Active</div>
        <div className="dc-stat"><span>{codes.reduce((s, c) => s + (c.usedCount || 0), 0)}</span>Total Uses</div>
      </div>

      {/* Table */}
      {loading ? (
        <p className="dc-loading">Loading…</p>
      ) : codes.length === 0 ? (
        <p className="dc-empty">No discount codes yet. Create one above.</p>
      ) : (
        <div className="dc-table-wrap">
          <table className="dc-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Status</th>
                <th>Uses</th>
                <th>Max Uses</th>
                <th>Expires</th>
                <th>Created by</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <tr key={c._id}>
                  <td><strong className="dc-code-text">{c.code}</strong></td>
                  <td><span className="dc-pct">{c.discountPercent}%</span></td>
                  <td>
                    {c.isActive
                      ? <span className="dc-badge dc-badge-active">Active</span>
                      : <span className="dc-badge dc-badge-inactive">Inactive</span>}
                  </td>
                  <td>{c.usedCount || 0}</td>
                  <td>{c.maxUses === 0 ? '∞' : c.maxUses}</td>
                  <td>
                    {c.expiresAt
                      ? new Date(c.expiresAt).toLocaleDateString('en-ZA')
                      : <span className="dc-na">—</span>}
                  </td>
                  <td>
                    {c.createdBy
                      ? `${c.createdBy.firstName || ''} (${c.createdBy.username || ''})`
                      : '—'}
                  </td>
                  <td className="dc-actions">
                    <button
                      className={`dc-action-btn ${c.isActive ? 'dc-deactivate' : 'dc-activate'}`}
                      onClick={() => handleToggle(c._id, c.code)}
                    >
                      {c.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      className="dc-action-btn dc-delete"
                      onClick={() => handleDelete(c._id, c.code)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
